import Bugsnag from '@bugsnag/js';
import { dehydrate } from '@tanstack/react-query';
import type { GetServerSideProps } from 'next';
import Head from 'next/head';
import { serverSideTranslations } from 'next-i18next/pages/serverSideTranslations';

import { BannerSlot } from '@/core/lib/ads/ads.context';
import Query from '@/core/lib/new-architecture/query';
import makeQueryClient from '@/core/lib/query/queryClient';
import base from '@/core/lib/translation/base';
import Catalogue from '@/core/pages/Catalogue';
import { categoryMapping, checkIfOldCategoryMappedToNew, checkIfOldCategoryShouldRedirectToNewCategory, newCategoryMapping } from '@/core/types/category';
import { donationsPublicDonationQuerySchema } from '@/core/types/donation';
import PublicLayout from '@/next/layouts/PublicLayout';

export const getServerSideProps: GetServerSideProps = async ({ query }) => {
  try {
    const donationsPublicDonationQuerySchemaCast = donationsPublicDonationQuerySchema.cast(query, { stripUnknown: true, assert: false });
    const queryClient = makeQueryClient();

    if (
      donationsPublicDonationQuerySchemaCast.cat &&
      !Object.values(newCategoryMapping).includes(donationsPublicDonationQuerySchemaCast.cat) &&
      checkIfOldCategoryMappedToNew(donationsPublicDonationQuerySchemaCast.cat)
    ) {
      const categoryMapped = categoryMapping[donationsPublicDonationQuerySchemaCast.cat];
      if (typeof categoryMapped === 'number' && checkIfOldCategoryShouldRedirectToNewCategory(categoryMapped)) {
        const newCategoryLabel = newCategoryMapping[categoryMapped];
        return {
          redirect: { permanent: true, destination: `/catalogue/${newCategoryLabel}/${query.loc}?sort=date` },
          props: {
            ...(await serverSideTranslations('fr', [...base, 'catalogue'])),
            path: `/catalogue/${newCategoryLabel}/${query.loc}?sort=date`,
          },
        };
      }

      return {
        redirect: { permanent: true, destination: `/catalogue/ville/${query.loc}?title=${categoryMapped}&sort=date&distance=20` },
        props: {
          ...(await serverSideTranslations('fr', [...base, 'catalogue'])),
          path: `/catalogue/ville/${query.loc}?title=${categoryMapped}&sort=date&distance=20`,
        },
      };
    }

    await Promise.all([
      await Query.categories.useCategories.prefetch?.({ queryClient }),
      await Query.locs.useDetail.prefetch?.({ queryClient, search: donationsPublicDonationQuerySchemaCast.loc }),
      await Query.donations.useDonations.prefetch?.({ queryClient, ...donationsPublicDonationQuerySchemaCast }),
    ]);

    const dehydratedState = dehydrate(queryClient);

    return {
      props: {
        ...(await serverSideTranslations('fr', [...base, 'catalogue'])),
        path: `/catalogue/${query.cat}/${query.loc}`,
        dehydratedState,
      },
    };
  } catch (err) {
    Bugsnag.notify(err as Error);

    return {
      props: {
        ...(await serverSideTranslations('fr', [...base, 'catalogue'])),
        path: `/catalogue/${query.cat}/${query.loc}`,
      },
    };
  }
};

Catalogue.getLayouts = page => {
  const adUnit = '/22963265510,23187251655/23187251655/od.donnons.org/recherche';
  const dataConfig = {
    adUnit,
  };
  return (
    <PublicLayout adSlot={BannerSlot.DONNONS_LIST_SIDE}>
      <Head>
        <script dangerouslySetInnerHTML={{ __html: 'var optidigitalQueue = optidigitalQueue || {}; optidigitalQueue.cmd = optidigitalQueue.cmd || [];' }}></script>
        <link rel="preload" href="https://securepubads.g.doubleclick.net/tag/js/gpt.js" as="script" type="text/javascript" />
        <script type="module" id="optidigital-ad-init" async src="//scripts.opti-digital.com/modules/?site=donnons" data-config={JSON.stringify(dataConfig)} key={adUnit}></script>
      </Head>
      {page}
    </PublicLayout>
  );
};

Catalogue.footer = { catalogue: true };

export default Catalogue;
